import axios from 'axios';

export default (publicKey, cartId, options = {}) => ({
    stripe: null,
    elements: null,
    expressCheckoutElement: null,
    initializing: true,
    available: false,
    processing: false,
    error: null,
    totalsUpdatedHandler: null,

    init() {
        if (typeof Stripe === 'undefined') {
            this.initializing = false;
            this.error = 'Stripe has not loaded. Please refresh and try again.';

            return;
        }

        this.removeTotalsUpdatedListener();

        this.totalsUpdatedHandler = async (event) => {
            if (this.expressCheckoutElement === null) return;

            options = {
                ...options,
                lineItems: event.detail.line_items,
                shippingFee: event.detail.shipping_fee,
                amount: event.detail.amount
            };

            await this.updateElementsAmount(options.amount);

            this.expressCheckoutElement.update?.({
                lineItems: options.lineItems,
                shippingRates: options.collectShipping ? this.shippingRates(options) : undefined
            });
        };

        window.addEventListener('checkout-totals-updated', this.totalsUpdatedHandler);

        this.stripe = Stripe(publicKey);

        try {
            this.initializeCheckout(cartId, options);
        } catch (e) {
            console.error(e);
            this.removeTotalsUpdatedListener();
            this.initializing = false;
            this.error = 'Unable to initialize express checkout. Please try another payment method.';
        }
    },

    destroy() {
        this.removeTotalsUpdatedListener();
        this.expressCheckoutElement?.destroy();
    },

    removeTotalsUpdatedListener() {
        if (this.totalsUpdatedHandler === null) return;

        window.removeEventListener('checkout-totals-updated', this.totalsUpdatedHandler);
        this.totalsUpdatedHandler = null;
    },

    shippingRates(options) {
        return [{
            id: options.shippingFee === 0 ? 'free' : 'standard',
            displayName: options.isPickup
                ? 'Free On-farm Pickup'
                : options.shippingFee === 0
                    ? 'Free Shipping'
                    : 'Standard Shipping',
            amount: options.shippingFee
        }];
    },

    async updateElementsAmount(amount) {
        if (this.elements === null || amount === null || typeof amount === 'undefined') return;

        await this.elements.update({ amount });
    },

    initializeCheckout(cartId, options) {
        if (this.processing) return;

        this.available = false;
        this.processing = false;
        this.error = null;

        const elementOptions = {
            buttonHeight: 48,
            buttonType: {
                applePay: 'check-out',
                googlePay: 'checkout'
            },
            paymentMethods: {
                applePay: 'always',
                googlePay: 'always',
                link: 'never',
                paypal: 'never',
                amazonPay: 'never',
                klarna: 'never'
            },
            emailRequired: true,
            phoneNumberRequired: true,
            billingAddressRequired: false
        };

        const lineItems = options.lineItems ?? null;


        elementOptions.lineItems = lineItems;

        if (options.collectShipping) {
            elementOptions.shippingAddressRequired = true;
            elementOptions.allowedShippingCountries = ['US'];
            elementOptions.shippingRates = this.shippingRates(options);
        }

        this.elements = this.stripe.elements({
            mode: 'payment',
            amount: options.amount, // cents, e.g. 10999
            currency: 'usd',
            paymentMethodTypes: ['card'],
            setupFutureUsage: 'off_session'
        });

        this.expressCheckoutElement = this.elements.create('expressCheckout', elementOptions);

        if (options.collectShipping) {
            this.expressCheckoutElement.on('shippingaddresschange', event => {

                axios.patch(`/api/carts/${cartId}/shipping-address`, {
                    address: {
                        street: event.address.line1 ?? '',
                        street_2: event.address.line2 ?? '',
                        city: event.address.city,
                        state: event.address.state,
                        postal_code: event.address.postal_code
                    }
                })
                    .then(async ({ data }) => {
                        if (data.eligible === false) {
                            event.reject();
                            this.error = data.message ?? 'Your selected address is invalid.';

                            return;
                        }

                        this.error = null;

                        options = {
                            ...options,
                            lineItems: data.line_items ?? options.lineItems,
                            shippingFee: data.shipping_fee,
                            amount: data.amount ?? options.amount,
                            isPickup: data.is_pickup
                        };

                        await this.updateElementsAmount(options.amount);

                        event.resolve({
                            lineItems: options.lineItems,
                            shippingRates: this.shippingRates(options)
                        });
                    })
                    .catch(e => {
                        event.reject();
                        this.error = 'Your selected address is invalid.';
                    });
            });
        }

        this.expressCheckoutElement.on('ready', ({ availablePaymentMethods }) => {
            this.available = !!availablePaymentMethods && Object.values(availablePaymentMethods).some(Boolean);
            this.initializing = false;
        });

        this.expressCheckoutElement.on('confirm', async (event) => {
            await this.handleConfirm(event);
        });

        this.expressCheckoutElement.on('cancel', () => {
            this.processing = false;
        });

        this.expressCheckoutElement.on('loaderror', ({ error }) => {
            this.available = false;
            this.error = error?.message || 'Express checkout could not load. Please try another payment method.';
            this.$dispatch('wallet-express-checkout-error', { error: this.error });
        });

        this.expressCheckoutElement.mount(this.$refs.expressCheckout);
    },

    async handleConfirm(event) {
        this.processing = true;
        this.error = null;

        try {
            const { error: submitError } = await this.elements.submit();

            if (submitError) {
                this.error = submitError.message;
                event.paymentFailed({ reason: 'fail' });
                return;
            }

            const { error: tokenError, confirmationToken } = await this.stripe.createConfirmationToken({
                elements: this.elements
            });

            if (tokenError) {
                this.error = tokenError.message;
                event.paymentFailed({ reason: 'fail' });
                return;
            }

            window.dispatchEvent(new CustomEvent('wallet-processing'));

            await this.$wire.handleExpressCheckout(
                confirmationToken.id,
                event.expressPaymentType,
                {
                    billing_details: event.billingDetails ?? null,
                    shipping_address: event.shippingAddress ?? null
                }
            );
        } catch (e) {
            this.error = 'Something went wrong. Please try again.';
            event.paymentFailed({ reason: 'fail' });
            window.dispatchEvent(new CustomEvent('wallet-processing-failed'));

        } finally {
            this.processing = false;
        }
    }
});
